/**
 * Hook: useIsDentalTenant()
 * Returns true if the currently logged-in user belongs to a tenant whose
 * sector is dental. Used to gate the dental suite (Migration + Odontogram +
 * Prescriptions tab in sidebar and patient profile).
 *
 * Detection layers (any match → dental):
 *   1. user.vertical === "dental"
 *   2. user.tenant_sector / sector slug in known list
 *   3. user.tenant_tier or subscription_tier === "dental_pro"
 *   4. user.features (or tenant features) includes "smart_prescription"
 *   5. user.tenant_name contains "dental"/"odonto"/"dentista" (heuristic
 *      last-resort for legacy tenants where vertical metadata is missing)
 */
import { useAuth } from "../App";

const DENTAL_SECTOR_SLUGS = new Set([
  "dentista", "dentisti", "dental", "odonto", "odontoiatria",
  "odontoiatra", "studio_dentistico", "clinica_odontoiatrica", "dentist",
]);

const DENTAL_NAME_TOKENS = ["dental", "odonto", "dentist", "dentale"];

export const useIsDentalTenant = () => {
  const { user } = useAuth();
  if (!user) return false;

  const sector   = (user.tenant_sector || user.sector || "").toString().trim().toLowerCase();
  const vertical = (user.vertical || user.tenant_vertical || "").toString().trim().toLowerCase();
  const tier     = (user.tenant_tier || user.subscription_tier || "").toString().trim().toLowerCase();
  const planId   = (user.selected_plan_id || "").toString().toLowerCase();
  const features = Array.isArray(user.features) ? user.features
                 : Array.isArray(user.tenant_features) ? user.tenant_features
                 : [];
  const tenantName = (user.tenant_name || user.tenantName || "").toString().toLowerCase();

  if (vertical === "dental") return true;
  if (DENTAL_SECTOR_SLUGS.has(sector)) return true;
  if (tier === "dental_pro" || tier.includes("dental")) return true;
  if (planId.startsWith("dental_")) return true;
  if (features.includes("smart_prescription")) return true;
  if (features.includes("odontogram")) return true;
  if (DENTAL_NAME_TOKENS.some(tok => tenantName.includes(tok))) return true;

  return false;
};

export const hasDentalPermission = (user, perm) => {
  if (!user) return false;
  const role = (user.role || "").toLowerCase();
  if (role === "super_admin" || role === "salon_admin") return true;
  const arr = Array.isArray(user.permissions) ? user.permissions : [];
  return arr.includes(perm);
};
